/**
 * Footer.tsx -- Footer layout primitive (v2 design).
 *
 * Matches the v2 design handoff footer: dark near-black background, a left
 * brand block (wordmark + tagline, 2fr) followed by three link columns
 * (Product / Resources / Company, 1fr each) with coral mono-uppercase
 * headings and white-70% links, then a ruled copyright line. Max-width 1280px,
 * centered. All design values reference CSS custom properties (var(--*)).
 *
 * Rendered on every non-auth page via AppShell (showFooter), matching the
 * proposed design where the footer appears on all screens.
 */
import React from "react";

/* ── Link column data ─────────────────────────────────────────────── */

interface FooterLink {
  label: string;
  href: string;
}

interface FooterColumn {
  heading: string;
  links: FooterLink[];
}

const COLUMNS: FooterColumn[] = [
  {
    heading: "Product",
    links: [
      { label: "Command Center", href: "#/today" },
      { label: "Workspaces", href: "#/workspaces" },
      { label: "Activity", href: "#/activity" },
      { label: "Library", href: "#/library" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Documentation", href: "#/docs" },
      { label: "Changelog", href: "#/changelog" },
      { label: "Support", href: "#/support" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", href: "#/about" },
      { label: "Trust & Security", href: "#/trust" },
      { label: "Contact", href: "#/contact" },
    ],
  },
];

/* ── Styles (all CSS custom-property references) ──────────────────── */

const footerStyle: React.CSSProperties = {
  background: "var(--color-primary)",
  color: "var(--fg-on-dark)",
  padding: "var(--space-4xl) var(--space-3xl) var(--space-2xl)",
  marginTop: "var(--space-section)",
};

const gridStyle: React.CSSProperties = {
  maxWidth: "1280px",
  marginLeft: "auto",
  marginRight: "auto",
  display: "grid",
  gridTemplateColumns: "2fr 1fr 1fr 1fr",
  gap: "var(--space-2xl)",
  boxSizing: "border-box" as const,
};

const wordmarkStyle: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontSize: "var(--fs-h3)",
  color: "var(--fg-on-dark)",
  margin: 0,
};

const dotStyle: React.CSSProperties = {
  color: "var(--color-coral)",
};

const taglineStyle: React.CSSProperties = {
  fontFamily: "var(--font-ui)",
  fontSize: "var(--fs-micro)",
  lineHeight: 1.5,
  color: "var(--fg-on-dark)",
  opacity: 0.7,
  maxWidth: "320px",
  margin: "var(--space-md) 0 0",
};

const columnHeadingStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-micro)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  color: "var(--color-coral)",
  margin: "0 0 var(--space-md)",
  fontWeight: 400,
};

const linkStyle: React.CSSProperties = {
  fontFamily: "var(--font-ui)",
  fontSize: "var(--fs-micro)",
  color: "var(--fg-on-dark)",
  opacity: 0.7,
  textDecoration: "none",
  display: "block",
  lineHeight: 2,
};

const copyrightStyle: React.CSSProperties = {
  maxWidth: "1280px",
  marginLeft: "auto",
  marginRight: "auto",
  marginTop: "var(--space-2xl)",
  paddingTop: "var(--space-xl)",
  borderTop: "1px solid rgba(255,255,255,0.12)",
  fontFamily: "var(--font-ui)",
  fontSize: "var(--fs-micro)",
  color: "var(--fg-on-dark)",
  opacity: 0.5,
};

/* ── Component ────────────────────────────────────────────────────── */

export default function Footer() {
  return (
    <footer data-testid="footer" style={footerStyle}>
      <div style={gridStyle}>
        {/* Brand block */}
        <div>
          <p style={wordmarkStyle}>
            capic&uacute;a<span style={dotStyle}>.</span>
          </p>
          <p style={taglineStyle}>
            An AI operations workspace for teams who ship serious work.
          </p>
        </div>

        {/* Link columns */}
        {COLUMNS.map((col) => (
          <div key={col.heading}>
            <h5 style={columnHeadingStyle}>{col.heading}</h5>
            <nav aria-label={`${col.heading} links`}>
              {col.links.map((link) => (
                <a key={link.label} href={link.href} style={linkStyle}>
                  {link.label}
                </a>
              ))}
            </nav>
          </div>
        ))}
      </div>

      {/* Copyright */}
      <div style={copyrightStyle}>
        &copy; 2026 Capic&uacute;a, Inc. All rights reserved.
      </div>
    </footer>
  );
}

export { Footer };
